import React, {useCallback, useContext, useEffect, useMemo, useState,} from "react";
import {Box, Card, Checkbox, FormControlLabel, makeStyles, Typography} from "@material-ui/core";
import {OrganizationRef, OrganizationTypeValues} from "api/organization";
import {User} from "api/organization/users";
import {Button, formatDate, GeoZoneMap, HeadBarProps, InputForm, Notification} from "components";
import {Theme, themeContext} from "@smartb/archetypes-ui-components";
import {Add, Block, Cached, CheckCircle, CheckCircleOutline, ErrorOutline} from "@material-ui/icons";
import {useTranslation} from "react-i18next";
import ReactDropzone, {FileRejection} from "react-dropzone";
import {tourClient} from "api/tour";
import withConnect from "./withConnect";
import DataTable from "react-data-table-component";
import {DeliveryCsvBonusOverview, DeliveryCsvLineError, DeliveryCsvScan} from "api/tour/command/scanCsv";
import {useAuth} from "auth/useAuth";
import csvEditTutorialPdf from '../../../assets/Tutoriel Édition CSV.pdf';
import scanToolTutorialPdf from '../../../assets/Tutoriel outil analyse.pdf';
import {geoZoneClient} from "../../../api/geoZone";
import {GeoLocation} from "../../../api/geoLocation";

const useStyles = (theme: Theme) =>
  makeStyles(() => ({
    section: {
      padding: "30px",
      display: "flex",
      flexDirection: "column",
      alignContent: "flex-start",
    },
    form: {
      display: "flex",
      alignContent: "flex-start",
      marginBottom: "20px",
    },
    container: {
      borderTop: "1px solid gray",
      padding: "30px 0",
    },
    title: {
      fontSize: "17px",
      marginTop: "20px",
      marginBottom: "20px",
      marginLeft: "2px",
      lineHeight: 1.4,
      fontWeight: 600,
    },
    sectionTitle: {
      borderBottom: "2px solid #98a5ab33",
      padding: "20px 0",
      marginBottom: "30px",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "end"
    },
    sectionText: {
      color: "#353945",
      fontWeight: 600,
      fontSize: "24px",
      lineHeight: "23px",
    },
    subSectionText: {
      color: "#353945",
      fontWeight: 600,
      fontSize: "20px",
      lineHeight: "23px",
      display: "flex",
      gap: "5px",
      alignItems: "center",
    },
    tutoButtons: {
      display: "flex",
      width: "100%",
      gap: "20px",
      alignItems: "center",
      justifyContent: "end"
    },
    button: {
      width: "fit-content",
      fontSize: "14px!important"
    },
    text: {
      color: "#353945",
      fontSize: "18px",
      fontStyle: "normal",
      fontWeight: 400,
      lineHeight: 1.4,
    },
    error: {
      color: "#bd1313",
      fontSize: "18px",
    },
    success: {
      color: "#4caf50",
      fontSize: "18px",
    },
    errorMessage: {
      color: "#EC8A90",
      fontSize: "18px",
      marginBottom: "15px",
    },
    organizationPicker: {
      width: "300px",
    },
    addFileContainer: {
      width: "fit-content",
      display: "flex",
      alignItems: "center",
      justifyContent: "end",
      gap: "20px",
      fontSize: "14px!important"
    },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)"
    },
    fileButton: {
      fontSize: "14px!important",
      height: "48px",
      width: "fit-content",
      padding: "7px 24px!important",
      border: `1px solid ${theme.secondaryColor}`,
      borderRadius: "4px",
      textTransform: "none",
      "& span": {
        color: theme.secondaryColor,
      },
    },
    addIcon: {
      marginRight: "10px",
      width: "24px",
      height: "24px",
      color: theme.secondaryColor,
    },
    divider: {
      marginBottom: "15px",
    },
    chip: {
      padding: "4px 8px",
      gap: "4px",
      borderRadius: "4px",
      background: "rgba(60, 120, 216, 0.10)",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-evenly",
      alignItems: "center",
      fontSize: "16px",
      fontWeight: 600,
    },
    loadingChip: {
      color: "#3C78D8",
      background: "rgba(60, 120, 216, 0.10)"
    },
    successChip: {
      color: "#159D50",
      background: "rgba(21, 157, 80, 0.10)"
    },
    errorChip: {
      color: "#BD2237",
      background: "rgba(228, 66, 88, 0.10)"
    },
    errorReportText: {
      color: "#BD2237",
      background: "rgba(228, 66, 88, 0.10)",
      borderRadius: "12px",
      padding: "12px",
      width: "fit-content"
    },
    warningReportText: {
      color: "#F90",
      background: "rgba(255, 153, 0, 0.10)",
      borderRadius: "12px",
      padding: "12px",
      width: "fit-content"
    },
    resultContainer: {
      marginTop: "20px",
    },
    resultCard: {
      width: "100%",
    },
    successScanReportSection: {
      display: "flex",
      flexDirection: "column"
    },
    fileSentNotification: {
      marginTop: "15px",
      fontSize: "14px!important"
    },
    mapContainer: {
      marginTop: "15px"
    }
  }));

enum ScannerState {
  LOADING,
  SUCCESS,
  ERROR
}

interface CsvScannerPageProps {
  currentUser: User | null;
  organizationRefs: Map<string, OrganizationRef>;
  setHeadBar: (headBar: HeadBarProps) => void;
}

const CsvScannerPage = (props: CsvScannerPageProps) => {
  const { currentUser, organizationRefs, setHeadBar } = props;
  const theme = useContext(themeContext);
  const classes = useStyles(theme)();
  const { t } = useTranslation();
  const [authService, keycloak] = useAuth();
  const [organizationId, setOrganizationId] = useState<string | undefined>();
  const [file, setFile] = useState<File | undefined>();
  const [validity, setValidity] = useState(false);
  const [error, setError] = useState<string>();
  const [scanState, setScanState] = useState<ScannerState | undefined>();
  const [scan, setScan] = useState<DeliveryCsvScan>();
  const [noAreaDeliveryCount, setNoAreaDeliveryCount] = useState<number>(0);
  const [sendFileClicked, setSendFileClicked] = useState<boolean>(false);
  const [fileSuccessfulySent, setFileSuccessfulySent] = useState<boolean>(false);
  const [skipDuplicateCheck, setSkipDuplicateCheck] = useState<boolean>(false);

  const { isColisActivAdmin, isCarrier, isOperator, isTms } = useMemo(() => ({
    isColisActivAdmin: authService.isColisActivAdmin(currentUser, keycloak),
    isCarrier: authService.isCarrier(currentUser, keycloak),
    isOperator: authService.isOperator(currentUser, keycloak),
    isTms: authService.isTms(currentUser, keycloak)
  }), [[authService, currentUser, keycloak]])

  useEffect(() => {
    setHeadBar({
      title: t("app_admin_layout_csv_scanner"),
    });
  }, [setHeadBar, t]);

  const getOrganizations = useMemo(() =>
      Array.from(organizationRefs.values()).filter(org =>
        [
          OrganizationTypeValues.CARRIER.type,
          OrganizationTypeValues.OPERATOR.type,
          OrganizationTypeValues.TMS.type
        ].includes(org.organizationType.type)
      ).map((el) => {
        return {
          label: el.displayName,
          value: el.organizationId,
        };
      }), [organizationRefs]);

  const handleOrganizationChange = useCallback((organizationId?: string) => {
    setOrganizationId(organizationId);
  }, []);

  useEffect(() => {
    if ((isCarrier || isOperator || isTms) && !isColisActivAdmin) {
      setOrganizationId(currentUser?.details.organizationId!!)
    }
  }, [currentUser, isCarrier, isOperator, isTms, isColisActivAdmin]);

  useEffect(() => {
    setValidity(!(!file || !currentUser));
  }, [file, currentUser]);

  useEffect(() => {
    setScan(undefined)
    setSendFileClicked(false)
    setFileSuccessfulySent(false)
    if (validity) {
      scanCsvFile()
    }
  }, [validity]);


  const downloadTutorial = useCallback(async (tutorial: "csv_edit_tutorial" | "scan_tool_tutorial") => {
    let file;
    let title = "Colisactiv - Tutoriel.pdf";
    switch (tutorial) {
      case "csv_edit_tutorial":
        file = csvEditTutorialPdf;
        title = "Colisactiv - Tutoriel édition CSV.pdf"
        break;
      case "scan_tool_tutorial":
        file = scanToolTutorialPdf;
        title = "Colisactiv - Tutoriel outil analyse CSV.pdf"
        break;
      default:
        break;
    }

    const a = document.createElement("a");
    a.href = file;
    a.download = title;
    a.click();

  }, []);

  const onFileAdded = useCallback((file: File) => {
    setValidity(false)
    setFile(file)
    setError(undefined)
    if (organizationId !== undefined) {
      setScanState(ScannerState.LOADING)
    }
  }, [organizationId]);

  const onReject = (files: FileRejection[]) => {
    setValidity(false)
    setScanState(undefined)
    setScan(undefined)
    setError("Une erreur est intervenue")
    files.forEach((file) => {
      let errors = []
      if (file.errors[0].code === "file-too-large") {
        errors.push("La taille de votre fichier ne doit pas dépasser 30 Mo")
      }
      if (file.errors[0].code === "too-many-files") {
        errors.push("Un seul fichier à la fois")
      }
      if (file.errors[0].code === "file-invalid-type") {
        errors.push("Seuls les fichiers au format csv sont acceptés")
      }
      setError(errors.join(", "))
    });
  };

  const scanCsvFile = useCallback(() => {
    if (!file || !organizationId) return;
    file.arrayBuffer().then((arrayBuffer) => {
      tourClient
        .scanCsv(organizationId, arrayBuffer, skipDuplicateCheck)
        .then((response: any) => {
          const scan: DeliveryCsvScan = JSON.parse(response)
          if (scan.result.bonusOverviews !== undefined || scan.result.errors !== undefined) {
            setScan(scan)
            const result = scan.result
            const noAreaCount = result.bonusOverviews
              .filter((bonusOverview) => bonusOverview.territoryName == "Hors Zone")
              .reduce((sum, bonusOverview) => sum + bonusOverview.deliveryCount, 0)
            setNoAreaDeliveryCount(noAreaCount)
            setScanState((result.errors.length === 0 && result.bonusOverviews.length > 0) ? ScannerState.SUCCESS : ScannerState.ERROR)
          } else {
            setScanState(ScannerState.ERROR)
          }
        }).catch((e)=> {
        setScanState(ScannerState.ERROR)
      });
    });
  }, [file, skipDuplicateCheck]);

  const scanChip = useMemo(() => {
    if (scanState == undefined) {
      return (<></>)
    }
    let label, className, icon;
    switch (scanState.valueOf()) {
      case ScannerState.LOADING:
        label = "En cours"
        className = classes.loadingChip
        icon = (<Cached style={{transform: "scale(0.8)"}}/>)
        break;
      case ScannerState.SUCCESS:
        label = "Fichier valide"
        className = classes.successChip
        icon = (<CheckCircleOutline style={{transform: "scale(0.8)"}}/>)
        break;
      case ScannerState.ERROR:
        label = "Fichier invalide"
        className = classes.errorChip
        icon = (<ErrorOutline style={{transform: "scale(0.8)"}}/>)
        break;
    }
    return (<div className={`${classes.chip} ${className}`}>{icon}{label}</div>)
  }, [scanState, classes])

  const downloadScanReport = useCallback(async () => {
    if (!scan?.result) return;

    let title = ""
    if (scanState === ScannerState.ERROR) {
      const errorCount = scan.result.errors.length
      title = `Rapport d'analyse du fichier d'import ${file?.name} - ${errorCount} erreur${errorCount>1?"s":""}`
    } else if (scanState === ScannerState.SUCCESS) {
      title = `Rapport d'analyse du fichier d'import ${file?.name} - Valide`
    }
    const objectUrl = await tourClient.getCsvScanReport(scan.id)

    if (objectUrl == null) {
      return;
    }

    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = `${title}.csv`;
    a.click();

  }, [scanState, scan, file]);

  const errorColumns = useMemo(() => {
    return [
      {
        name: t("app_csv_scanner_error_type"),
        width: "30vw",
        cell: (error: DeliveryCsvLineError) => <p>{error.error}</p>,
      },
      {
        name: t("app_csv_scanner_error_comment"),
        width: "30vw",
        cell: (error: DeliveryCsvLineError) => <p>{error.value}</p>,
      },
      {
        name: t("app_csv_scanner_error_line"),
        width: "30vw",
        cell: (error: DeliveryCsvLineError) => <p style={{whiteSpace: "nowrap", overflowX: "scroll"}}>{error.line}</p>,
      },
    ];
  }, [scan]);

  const bonusColumns = useMemo(() => {
    return [
      {
        name: t("app_csv_scanner_operator"),
        width: "200px",
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.operatorName}</p>,
      },
      {
        name: t("app_csv_scanner_carrier"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.carrierName}</p>,
      },
      {
        name: t("app_csv_scanner_territory"),
        width: "300px",
        cell: (bonus: DeliveryCsvBonusOverview) => <p style={{color: bonus.territoryName == "Hors Zone" ? '#F90' : 'default'}}>{bonus.territoryName}</p>,
      },
      {
        name: t("app_csv_scanner_delivery_hour_count"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.duration}</p>,
      },
      {
        name: t("app_csv_scanner_delivery_count"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.deliveryCount}</p>,
      },
      {
        name: t("app_csv_scanner_deliverypack_count"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.packCount}</p>,
      },
      {
        name: t("app_csv_scanner_tour_count"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.nbTours}</p>,
      },
      {
        name: t("app_csv_scanner_average_points_per_tour"),
        cell: (bonus: DeliveryCsvBonusOverview) => {
          const avg = bonus.averagePointsPerTour;
          const formatted = (avg < 2 && avg >= 1.995) ? avg?.toFixed(5) : avg?.toFixed(2);
          return <p>{formatted}</p>;
        },
      },
      {
        name: t("app_csv_scanner_average_approach_distance"),
        cell: (bonus: DeliveryCsvBonusOverview) => <p>{bonus.averageApproachDistance?.toFixed(0)}m</p>,
      },
    ];
  }, [scan]);

  const sendFile = useCallback(() => {
    if (!file || !scan) return;
    setSendFileClicked(true)
    tourClient.sendCsvWithReport(scan.id).then((isSuccess: boolean) => {
      setFileSuccessfulySent(isSuccess)
    })
  }, [scan, file]);

  const { territoriesGeoJson, pointsGeoJson, hubsGeoJson } = useScanGeoJson(scan)
  const pointsLayers = useMemo(() => {
    if (!pointsGeoJson) return undefined

    return [
      { geoJson: pointsGeoJson, color: '#3C78D8' },
      ...(hubsGeoJson ? [{ geoJson: hubsGeoJson, color: '#ffe100' }] : [])
    ]
  }, [pointsGeoJson, hubsGeoJson])

  return (
    <Box style={{maxWidth: "2000px"}}>
      <Box className={classes.section}>
        <Box className={classes.sectionTitle}>
            <span className={classes.sectionText}>
              {t("app_csv_scanner_informations")}
            </span>
          <Box className={classes.tutoButtons}>
            <Button
              className={classes.button}
              style={{}}
              onClick={() => downloadTutorial("csv_edit_tutorial")}
            >
              {t("app_csv_scanner_csv_edit_tutorial")}
            </Button>
            <Button
              className={classes.button}
              style={{}}
              onClick={() => downloadTutorial("scan_tool_tutorial")}
            >
              {t("app_csv_scanner_scan_tool_tutorial")}
            </Button>
          </Box>
        </Box>
        <Box>
          <p className={classes.text}>
            {t("app_csv_scanner_guide_3")}<br/><br/>
            {t("app_csv_scanner_guide_4")}<br/>
            {t("app_csv_scanner_guide_5")}<br/><br/>
            {t("app_csv_scanner_guide_6")}<br/><br/>
            {t("app_csv_scanner_guide_7")}
          </p>
        </Box>
        <Box className={classes.sectionTitle}>
          <span className={classes.sectionText}>
            {t("app_csv_scanner_deposite_csv")}
          </span>
          <Box className={classes.addFileContainer}>
            <Box>
              {!validity && error && (
                <Typography variant="body2" className={classes.error}>
                  {error}
                </Typography>
              )}
            </Box>
            { isColisActivAdmin && (
              <Box>
                <InputForm
                  inputType={"select"}
                  onChange={handleOrganizationChange}
                  selectOptions={getOrganizations}
                  value={organizationId}
                  onRemoveValue={() => handleOrganizationChange(undefined)}
                  label={""}
                  placeHolder={t("app_csv_scanner_impersonate_organization")}
                  className={classes.organizationPicker}
                  id={"organizationPicker"}
                />
              </Box>
            )}
            <ReactDropzone
              onDropAccepted={(files) => {
                onFileAdded(files[0]);
              }}
              onDropRejected={(fileRejections) => {
                onReject(fileRejections);
              }}
              multiple={false}
              accept={"text/csv"}
              maxSize={30000000}
            >
              {({ getRootProps, getInputProps }) => (
                <div className={classes.buttonContainer} {...getRootProps()}>
                  <Button
                    onClick={() => {}}
                    className={classes.fileButton}
                    icon={<Add className={classes.addIcon} />}
                    variant="outlined"
                  >
                    {t("app_csv_add_csv")}
                    <input {...getInputProps()} />
                  </Button>
                </div>
              )}
            </ReactDropzone>
          </Box>
        </Box>
        {isColisActivAdmin && (
          <FormControlLabel
            control={
              <Checkbox
                checked={skipDuplicateCheck}
                onChange={(e) => setSkipDuplicateCheck(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={t("app_csv_scanner_skip_duplicate_check")}
          />
        )}
      </Box>
      {scanState != undefined && (
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <span className={classes.subSectionText}>
              {t("app_csv_scanner_scan_file")} - {file?.name}
            </span>
            {scanChip}
          </Box>
          {scan && (
            <Box>
              {scanState == ScannerState.ERROR && (
                <p className={classes.text}>
                  {t("app_csv_scanner_error_text_1", {errorCount: scan.result.errors.length})}<br/><br/>
                  {t("app_csv_scanner_error_text_2")}
                </p>
              )}
              {scanState == ScannerState.SUCCESS && (
                <p className={classes.text}>
                  {t("app_csv_scanner_success_text_1")}<br/><br/>
                  {t("app_csv_scanner_success_text_2")}<br/><br/>
                  {t("app_csv_scanner_success_text_3")}<br/><br/>
                  <b>{t("app_csv_scanner_success_text_4")}
                    {t("app_csv_scanner_success_text_5", {
                      deliveryCount: scan.result.bonusOverviews.map((b)=> b.deliveryCount).reduce((sum, dCount) => sum + dCount),
                      period: formatDate(scan.result.bonusOverviews[0].period.startDate)?.toString()
                    })}</b>.<br/>
                  <b>{t("app_csv_scanner_tour_stats", {
                    nbTours: scan.result.nbTours,
                    averagePointsPerTour: (() => {
                      const avg = scan.result.bonusOverviews.map((b)=> b.deliveryCount).reduce((sum, dCount) => sum + dCount) / scan.result.nbTours;
                      return (avg < 2 && avg >= 1.995) ? avg.toFixed(5) : avg.toFixed(2);
                    })(),
                    averageApproachDistance: (() => {
                      const totalDeliveries = scan.result.bonusOverviews.reduce((sum, b) => sum + b.deliveryCount, 0);
                      const weightedSum = scan.result.bonusOverviews.reduce((sum, b) => sum + (b.averageApproachDistance || 0) * b.deliveryCount, 0);
                      return totalDeliveries > 0 ? (weightedSum / totalDeliveries).toFixed(0) : "0";
                    })()
                  })}</b>
                </p>
              )}
            </Box>
          )}
        </Box>
      )}
      {scan && (
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <span className={classes.sectionText}>
              {t("app_csv_scanner_scan_report")}
            </span>
            <Button
              className={classes.button}
              onClick={downloadScanReport}
            >
              {t("app_csv_scanner_download_report")}
            </Button>
          </Box>
          {scanState == ScannerState.ERROR && (
            <Box>
              <p className={classes.errorReportText}>
                {t("app_csv_scanner_blocking_errors_text")}
              </p>
              <Box className={classes.sectionTitle}>
                <span className={classes.subSectionText}>
                <Block/> {t("app_csv_scanner_blocking_errors")}
                </span>
                <div className={classes.errorReportText}>{scan.result.errors.length}</div>
              </Box>
              <Card className={classes.resultCard}>
                <DataTable
                  columns={errorColumns}
                  data={scan.result.errors}
                  noDataComponent={<p style={{ padding: "10px" }}></p>}
                  noHeader
                  theme="colisactiv"
                />
              </Card>
              {territoriesGeoJson && pointsLayers && <GeoZoneMap
                  areaGeoJson={territoriesGeoJson}
                  pointsLayers={pointsLayers}
                  mapContainerClassName={classes.mapContainer}
              />}
            </Box>
          )}
          {scanState == ScannerState.SUCCESS && (
            <Box className={classes.successScanReportSection}>
              {noAreaDeliveryCount > 0 && (
                <p className={classes.warningReportText}>
                  {t("app_csv_scanner_no_area_delivery_count", {noAreaDeliveryCount: noAreaDeliveryCount})}
                </p>
              )}
              {scan.result.lowAveragePointsAlert && (
                <p className={classes.warningReportText} style={{marginTop: noAreaDeliveryCount > 0 ? "10px" : "0"}}>
                  {t("app_csv_scanner_low_average_points_alert")}
                </p>
              )}
              <Card className={classes.resultCard}>
                <DataTable
                  columns={bonusColumns}
                  data={scan.result.bonusOverviews}
                  noDataComponent={<p style={{ padding: "10px" }}></p>}
                  noHeader
                  theme="colisactiv"
                />
              </Card>
              {territoriesGeoJson && <GeoZoneMap
                areaGeoJson={territoriesGeoJson}
                pointsLayers={pointsLayers}
                mapContainerClassName={classes.mapContainer}
              />}
              <Button
                className={classes.button}
                style={{alignSelf: "end", marginTop: "40px"}}
                onClick={sendFile}
                disabled={sendFileClicked}
              >
                {t("app_csv_scanner_send_file")}
              </Button>

              {(sendFileClicked && fileSuccessfulySent) && (
                <Notification
                  severity={"success"}
                  icon={<CheckCircle style={{ color: "rgb(76, 175, 80)" }} />}
                  className={classes.fileSentNotification}
                >
                  {t("app_csv_scanner_file_sent_success_text_1")}<br/>
                  {t("app_csv_scanner_file_sent_success_text_2")}
                </Notification>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const useScanGeoJson = (scan: DeliveryCsvScan | undefined) => {
  const [territoriesData, setTerritoriesData] = useState<Record<string, Record<string, number[][][]>> | undefined>()

  useEffect(() => {
    if (!scan) return setTerritoriesData(undefined)

    const territoryCodes = scan.result.bonusOverviews
      .map(bonusOverview => bonusOverview.territoryCode)
      .filter(code => code != null) as string[]

    if (territoryCodes.length === 0) return setTerritoriesData(undefined)

    geoZoneClient.query.getTerritoriesData(territoryCodes)
      .then(data => { setTerritoriesData(data) })
  }, [scan])

  const territoriesGeoJson = useMemo(() => {
    if (!territoriesData) return undefined

    const features: string[] = []

    Object.values(territoriesData).forEach(geozonesData => {
      Object.values(geozonesData).map((geozoneData) => {
        features.push(`{
            "type": "Feature",
            "geometry": {
                "type": "MultiPolygon",
                "coordinates": [${JSON.stringify(geozoneData)}]
            }
        }`)
      })
    })

    return `{
        "type": "FeatureCollection",
        "features": [${features}]
    }`
  }, [territoriesData])

  const hubsGeoJson = useMemo(() => scan && geoLocationsToGeoJson(scan.result.hubs.map(h => h.location)), [scan])
  const pointsGeoJson = useMemo(() => scan && geoLocationsToGeoJson(scan.result.deliveryPoints.map(p => p.location)), [scan])

  return useMemo(() => ({
    territoriesGeoJson,
    hubsGeoJson,
    pointsGeoJson
  }), [territoriesGeoJson, pointsGeoJson, hubsGeoJson])
}

function geoLocationsToGeoJson(points: GeoLocation[]) {
  if (points.length === 0) return undefined

  return `{
      "type": "FeatureCollection",
      "features": [${points.map(point => `{
        "type": "Feature",
        "geometry": {
            "type": "Point",
            "coordinates": [${point.lon}, ${point.lat}]
        }
      }`)}]
    }`
}

export default withConnect(CsvScannerPage);
